import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BalansEnResultatenrekening,
  BokkzVpbDataService,
  CorporateTaxEntity,
  VpbAangifte
} from '@snelstart-ventures/fiscaal/domain-logic';
import {AbstractControl, FormArray, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {debounceTime, filter, Observable, shareReplay, take} from 'rxjs';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-open-activa',
  templateUrl: './spec-open-activa.component.html',
  styleUrls: ['./spec-open-activa.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedUiFormControlsModule,
    SharedUiDetailsModule
  ]
})
export class SpecOpenActivaComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);
  public index = 0;

  public balansEnResultatenRekeningFg = new FormGroup({
    balansEnResultatenrekening: new FormArray<FormGroup>([]),
  });

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.corporateTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((data) => {
      this.updateForm(data.aangifte!);
    });
    this.balansEnResultatenRekeningFg.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.recalculateTotalen();
      this.saveForm();
    });
  }

  get balansEnResultatenrekening(): FormArray<FormGroup> {
    return this.balansEnResultatenRekeningFg.get('balansEnResultatenrekening') as FormArray;
  }

  getControls(rowControl: AbstractControl, fieldName: string): AbstractControl[] {
    const fieldArray = (rowControl as FormGroup).get(fieldName) as FormArray | null;
    return fieldArray?.controls ?? [];
  }

  public readonly activaSections: ReadonlyArray<ReadonlyArray<{ label: string; controlName: string }>> = [
    [
      { label: 'Goodwill', controlName: 'goodwill' },
      { label: 'Overige immateriele activa', controlName: 'overigeImmaterieleActiva' },
      { label: 'Gebouwen en terreinen', controlName: 'gebouwenEnTerreinen' },
      { label: 'Machines en installaties', controlName: 'machinesEnInstallaties' },
      { label: 'Andere vaste activa', controlName: 'andereVasteActiva' },
    ],
    [
      { label: 'Langlopende vord. groepsmaatschappijen', controlName: 'vorderingenLangGroepsmaatschappijen' },
      { label: 'Langlopende vord. participanten', controlName: 'vorderingenLangParticipanten' },
      { label: 'Overige financiële activa', controlName: 'overigeFinancieelVasteActiva' },
      { label: 'Deelnemingen', controlName: 'deelnemingen' },
    ],
    [
      { label: 'Voorraden', controlName: 'voorraden' },
      { label: 'Onderhanden werk', controlName: 'onderhandenWerk' },
      { label: 'Debiteuren', controlName: 'vorderingDebiteuren' },
      { label: 'Omzetbelasting', controlName: 'vorderingenOmzetbelasting' },
      { label: 'Kortlopende vord. groepsmaatschappijen', controlName: 'vorderingenKortGroepsmaatschappijen' },
      { label: 'Kortlopende vord. participanten', controlName: 'vorderingenKortParticipanten' },
      { label: 'Overige vorderingen', controlName: 'vorderingenOverig' },
    ],
    [
      { label: 'Effecten', controlName: 'effecten' },
      { label: 'Liquide middelen', controlName: 'liquideMiddelen' },
    ],
  ];

  private get activaFields(): string[] {
    return this.activaSections.flat().map(f => f.controlName);
  }

  private isFiscaleEenheid(): boolean {
    return this.dataService.isFiscaleEenheid.value;
  }

  private getConsolidatedRow(rows: BalansEnResultatenrekening[]): BalansEnResultatenrekening | undefined {
    return rows.find(row => row.naamVennootschap === 'Consolidatie');
  }

  private calculateConsolidatedOpeningsByField(rows: BalansEnResultatenrekening[], fieldName: string): number {
    return rows.reduce((sum, row) => {
      if (row.naamVennootschap === 'Consolidatie') {
        return sum;
      }

      const multiplier = row.naamVennootschap === 'Eliminatie' ? -1 : 1;
      const value =
        ((row as unknown as Record<string, Array<{ openingsbalans?: number | null }> | undefined>)[fieldName]
          ?.at(0)
          ?.openingsbalans ?? 0);
      return sum + value * multiplier;
    }, 0);
  }

  private buildConsolidatedFromRows(
    rows: BalansEnResultatenrekening[],
    base: BalansEnResultatenrekening
  ): BalansEnResultatenrekening {
    const consolidated = {
      ...base,
      naamVennootschap: 'Consolidatie',
    } as BalansEnResultatenrekening;

    const consolidatedRecord = consolidated as unknown as Record<
      string,
      Array<{ openingsbalans?: number | null; slotbalans?: number | null; totaalActivaOpen?: number | null }> | undefined
    >;

    let totaalActivaOpen = 0;
    for (const fieldName of this.activaFields) {
      const openingsbalans = this.calculateConsolidatedOpeningsByField(rows, fieldName);
      const existingField = consolidatedRecord[fieldName]?.at(0) ?? {};
      consolidatedRecord[fieldName] = [{
        ...existingField,
        openingsbalans: openingsbalans === 0 ? undefined : openingsbalans,
      }];
      totaalActivaOpen += openingsbalans;
    }

    const existingTotaal = consolidatedRecord['totaal']?.at(0) ?? {};
    consolidatedRecord['totaal'] = [{
      ...existingTotaal,
      totaalActivaOpen: totaalActivaOpen === 0 ? undefined : totaalActivaOpen,
    }];

    return consolidated;
  }

  private getInitialRows(vpbAangifte: VpbAangifte): BalansEnResultatenrekening[] {
    if (this.isFiscaleEenheid()) {
      const enkelvoudigeRows = [...(vpbAangifte.enkelvoudigeResultatenFiscaleEenheid || [])]
        .filter(row => row.naamVennootschap !== 'Consolidatie')
        .sort((a, b) => {
          const aIsEliminatie = a.naamVennootschap === 'Eliminatie' ? 1 : 0;
          const bIsEliminatie = b.naamVennootschap === 'Eliminatie' ? 1 : 0;
          return aIsEliminatie - bIsEliminatie;
        });
      const consolidatedRow = {
        ...vpbAangifte.balansEnResultatenrekening,
        naamVennootschap: 'Consolidatie',
      } as BalansEnResultatenrekening;

      return [...enkelvoudigeRows, consolidatedRow];
    }

    return [
      {
        ...vpbAangifte.balansEnResultatenrekening,
        naamVennootschap:
          vpbAangifte.balansEnResultatenrekening?.naamVennootschap ??
          vpbAangifte.identificatie?.naamLichaam,
      } as BalansEnResultatenrekening,
    ];
  }

  public getMaxValueForField(controlName: string): number {
    return this.balansEnResultatenrekening.controls
      .filter(ctrl => {
        const naam = ctrl.get('naamVennootschap')?.value;
        return naam !== 'Eliminatie' && naam !== 'Consolidatie';
      })
      .reduce((sum, ctrl) => {
        const fieldArray = ctrl.get(controlName) as FormArray | null;
        const value = fieldArray?.at(0)?.get('openingsbalans')?.value ?? 0;
        return sum + (value > 0 ? value : 0);
      }, 0);
  }

  public calculateTotaalActivaOpen(entityControl: AbstractControl): number {
    const entityFormGroup = entityControl as FormGroup;
    return this.activaFields.reduce((total, fieldName) => {
      const fieldArray = entityFormGroup.get(fieldName) as FormArray | null;
      if (!fieldArray) return total;
      for (let j = 0; j < fieldArray.length; j++) {
        total += fieldArray.at(j).get('openingsbalans')?.value ?? 0;
      }
      return total;
    }, 0);
  }

  private syncRowDisabledState(entityGroup: FormGroup): void {
    const isConsolidatie = entityGroup.get('naamVennootschap')?.value === 'Consolidatie';

    for (const fieldName of this.activaFields) {
      const fieldArray = entityGroup.get(fieldName) as FormArray | null;
      const openingsbalansControl = fieldArray?.at(0)?.get('openingsbalans');
      if (!openingsbalansControl) {
        continue;
      }

      if (isConsolidatie) {
        openingsbalansControl.disable({emitEvent: false});
      } else {
        openingsbalansControl.enable({emitEvent: false});
      }
    }
  }

  private recalculateTotalen(): void {
    const formArray = this.balansEnResultatenrekening;
    const isFe = this.isFiscaleEenheid();

    let consolidatieIndex = -1;
    for (let i = 0; i < formArray.length; i++) {
      const entityGroup = formArray.at(i) as FormGroup;
      if (entityGroup.get('naamVennootschap')?.value === 'Consolidatie') {
        consolidatieIndex = i;
        break;
      }
    }

    for (let i = 0; i < formArray.length; i++) {
      if (isFe && i === consolidatieIndex) {
        continue;
      }

      const entityGroup = formArray.at(i) as FormGroup;
      const totaal = this.calculateTotaalActivaOpen(entityGroup);
      const totaalArray = entityGroup.get('totaal') as FormArray | null;
      if (totaalArray && totaalArray.length > 0) {
        totaalArray.at(0).get('totaalActivaOpen')?.setValue(totaal, {emitEvent: false});
      }
    }

    if (isFe && consolidatieIndex >= 0) {
      this.recalculateConsolidatieRow(consolidatieIndex);
    }
  }

  private recalculateConsolidatieRow(consolidatieIndex: number): void {
    const formArray = this.balansEnResultatenrekening;
    const consolidatieGroup = formArray.at(consolidatieIndex) as FormGroup;
    let totaalActivaOpen = 0;

    for (const fieldName of this.activaFields) {
      let sum = 0;
      for (let i = 0; i < formArray.length; i++) {
        if (i === consolidatieIndex) continue;
        const rowGroup = formArray.at(i) as FormGroup;
        const multiplier = rowGroup.get('naamVennootschap')?.value === 'Eliminatie' ? -1 : 1;
        const fieldArray = rowGroup.get(fieldName) as FormArray | null;
        const value = fieldArray?.at(0)?.get('openingsbalans')?.value ?? 0;
        sum += value * multiplier;
      }

      const consolidatieField = (consolidatieGroup.get(fieldName) as FormArray | null)?.at(0);
      consolidatieField?.get('openingsbalans')?.setValue(sum, {emitEvent: false});
      totaalActivaOpen += sum;
    }

    const totaalArray = consolidatieGroup.get('totaal') as FormArray | null;
    totaalArray?.at(0)?.get('totaalActivaOpen')?.setValue(totaalActivaOpen, {emitEvent: false});
  }

  private updateForm(vpbAangifte: VpbAangifte): void {
    const lijst = this.getInitialRows(vpbAangifte);
    const formArray = this.balansEnResultatenrekening;

    this.index = lijst.length;
    if (this.index < 2) {
      this.index = 2;
    }

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i);
        control.patchValue(lijst[i], {emitEvent: false});
        this.syncRowDisabledState(control);
      } else {
        const formGroup = this.dataService.createBalansEnResultatenRekeningFormGroup(lijst[i]);
        formArray.push(formGroup as FormGroup, {emitEvent: false});
        this.syncRowDisabledState(formGroup as FormGroup);
      }
    }
  }

  ngOnDestroy(): void {
    this.saveForm();
    this.dataService.calculateLatestCorporateTaxReturn().subscribe();
  }

  saveForm(): void {
    this.balansEnResultatenRekeningFg.updateValueAndValidity({emitEvent: false});
    const rows =
      (this.balansEnResultatenRekeningFg.getRawValue().balansEnResultatenrekening ?? []) as BalansEnResultatenrekening[];
    const isFe = this.isFiscaleEenheid();
    const enkelvoudigeRows = rows.filter(row => row.naamVennootschap !== 'Consolidatie');

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const consolidatedRow = this.getConsolidatedRow(rows);
      const fallbackLastRow = rows.length > 0 ? rows[rows.length - 1] : undefined;
      const balansEnResultatenrekening = isFe
        ? this.buildConsolidatedFromRows(
          rows,
          consolidatedRow ?? fallbackLastRow ?? currentEntity.aangifte.balansEnResultatenrekening
        )
        : (rows[0] ?? currentEntity.aangifte.balansEnResultatenrekening);

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          balansEnResultatenrekening,
          enkelvoudigeResultatenFiscaleEenheid: isFe ? enkelvoudigeRows : undefined,
        }
      } as CorporateTaxEntity;
    });
  }
}
